import { useState, useEffect, useCallback } from "react";
import { getCustomerAPI } from "layouts/profile/_request";
import { useAuth } from "context";
import { resendOtp } from "layouts/authentication/_request";
import { verifyCustomerOtp } from "layouts/authentication/_request";

// Key for local storage to track if user has dismissed the verification
const PHONE_VERIFICATION_DISMISSED_KEY = "phone_verification_dismissed";

export const usePhoneVerification = () => {
  const { showSnackbarWithMessage, setVerifiedCustomer, verifiedCustomer } = useAuth();

  // Early return with dummy functions if in simulate mode
  if (verifiedCustomer?.simulateUser) {
    return {
      customer: null,
      isLoading: false,
      showOtpModal: false,
      verificationIdentifier: "",
      verificationType: "phone",
      setShowOtpModal: () => {},
      handleOtpVerify: () => {},
      handleSendOTP: () => {},
      getCustomerDetails: () => Promise.resolve(null),
      handleDismissOtpModal: () => {},
      resetDismissedState: () => {},
      isDismissed: false,
      holdPhoneVerification: () => {},
      resumePhoneVerification: () => {},
      checkPhoneVerification: () => {},
    };
  }

  // Continue with normal implementation when not in simulate mode
  const [customer, setCustomer] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [verificationIdentifier, setVerificationIdentifier] = useState("");
  const [verificationType, setVerificationType] = useState("phone");
  const [isInitialized, setIsInitialized] = useState(false);
  const [isDismissed, setIsDismissed] = useState(() => {
    // Check local storage on initialization to see if user previously dismissed
    return localStorage.getItem(PHONE_VERIFICATION_DISMISSED_KEY) === "true";
  });
  // New state to manage verification sequence
  const [isVerificationHeld, setIsVerificationHeld] = useState(false);
  const [verificationState, setVerificationState] = useState({
    phone: {
      otpNumber: "",
      otpSent: false,
    },
  });

  // Get customer details function
  const getCustomerDetails = async () => {
    try {
      const {
        data: { success, data },
      } = await getCustomerAPI();

      if (success) {
        const customerData = {
          id: data.customer.id,
          firstName: data.customer.firstName,
          lastName: data.customer.lastName,
          email: data.customer.email,
          verifiedAt: data.customer.verifiedAt,
          phoneVerifiedAt: data.customer.phoneVerifiedAt,
          phoneCountryCode: data.customer.phoneCountryCode,
          phoneNumber: data.customer.phoneNumber,
          isPass: data.customer.isPass,
        };
        setCustomer(customerData);
        return customerData;
      }
      return null;
    } catch (err) {
      console.error("Error fetching customer details:", err);
      showSnackbarWithMessage({
        type: "error",
        message: "Failed to fetch customer details",
      });
      return null;
    }
  };

  // New function to hold phone verification
  const holdPhoneVerification = useCallback(() => {
    setIsVerificationHeld(true);
  }, []);

  // New function to resume phone verification
  const resumePhoneVerification = useCallback(() => {
    setIsVerificationHeld(false);
    // If already initialized, trigger verification immediately
    if (isInitialized && customer) {
      checkPhoneVerification();
    }
  }, [isInitialized, customer]);

  // Handle sending OTP for phone verification
  const handlePhoneReSendOTP = async (phoneNumber, email) => {
    // If user has dismissed or verification is held, don't proceed
    if (isDismissed || isVerificationHeld) {
      return;
    }

    const phoneToUse = phoneNumber || customer?.phoneNumber;
    const emailToUse = email || customer?.email;

    if (!phoneToUse || !emailToUse) {
      return;
    }

    setIsLoading(true);
    try {
      const { data } = await resendOtp(false, emailToUse, phoneToUse);
      if (data?.success) {
        // Set modal state for phone verification
        setVerificationType("phone");
        setVerificationIdentifier(phoneToUse);
        // Ensure we set this to true and it triggers a re-render
        setShowOtpModal(true);
        // Force a re-render by updating another state
        setVerificationState((prev) => ({
          ...prev,
          phone: {
            ...prev.phone,
            otpSent: true,
          },
        }));

        showSnackbarWithMessage({
          type: "success",
          message: data?.message || "OTP sent successfully",
        });
      } else {
        showSnackbarWithMessage({
          type: "error",
          message: data?.message || "Failed to send OTP",
        });
      }
    } catch (e) {
      console.error("Error sending OTP:", e);
      showSnackbarWithMessage({
        type: "error",
        message: e.message || "An error occurred while sending OTP",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Check if phone verification is needed (extracted as separate function)
  const checkPhoneVerification = useCallback(() => {
    // Don't proceed if verification is on hold or dismissed
    if (isVerificationHeld || isDismissed || !customer) {
      return;
    }

    if (
      customer.phoneNumber &&
      customer.phoneCountryCode &&
      (customer.phoneVerifiedAt === null || !customer.phoneVerifiedAt)
    ) {
      // Small delay to ensure component is fully rendered
      setTimeout(() => {
        handlePhoneReSendOTP(customer.phoneNumber, customer.email);
      }, 500);
    }
  }, [customer, isVerificationHeld, isDismissed]);

  // Handle OTP verification
  const handleOtpVerify = async (otpCode) => {
    if (!customer?.phoneNumber) return;

    setIsLoading(true);
    try {
      const {
        data: { success, message },
      } = await verifyCustomerOtp({
        code: +otpCode,
        forgotPassword: false,
        userNumber: customer.phoneNumber,
      });

      if (success) {
        setVerifiedCustomer((prev) => ({
          ...prev,
          phoneVerifiedAt: new Date().toISOString(),
        }));

        // Reset verification state
        setVerificationState((prev) => ({
          ...prev,
          phone: {
            otpNumber: "",
            otpSent: false,
          },
        }));

        // Refresh customer details
        await getCustomerDetails();
        setShowOtpModal(false);

        showSnackbarWithMessage({
          type: "success",
          message,
        });
      } else {
        showSnackbarWithMessage({
          type: "error",
          message,
        });
      }
    } catch (e) {
      showSnackbarWithMessage({
        type: "error",
        message: e.message || "An error occurred during verification",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // New function to handle when user cancels/dismisses the OTP modal
  const handleDismissOtpModal = () => {
    // Mark as dismissed in state
    setIsDismissed(true);
    // Save to localStorage so it persists across page refreshes
    localStorage.setItem(PHONE_VERIFICATION_DISMISSED_KEY, "true");
    // Close the modal
    setShowOtpModal(false);
  };

  // Function to reset the dismissed state (can be called when cache/store is cleared)
  const resetDismissedState = () => {
    localStorage.removeItem(PHONE_VERIFICATION_DISMISSED_KEY);
    setIsDismissed(false);
  };

  // For handling manual OTP resend requests
  const handleSendOTP = () => {
    return handlePhoneReSendOTP();
  };

  // Split the initialization to ensure it happens after the component is fully mounted
  useEffect(() => {
    const initializeCustomerData = async () => {
      try {
        const customerData = await getCustomerDetails();
        setIsInitialized(true);
      } catch (error) {
        console.error("Failed to initialize customer data:", error);
      }
    };

    initializeCustomerData();
  }, []);

  // Modified effect to check for phone verification - now respects isVerificationHeld
  useEffect(() => {
    // Only proceed if we have initialized the customer data, user hasn't dismissed,
    // and verification is not on hold
    if (!isInitialized || !customer || isDismissed || isVerificationHeld) {
      return;
    }

    // Now we can proceed with the check
    checkPhoneVerification();
  }, [isInitialized, customer, isDismissed, isVerificationHeld, checkPhoneVerification]);

  return {
    customer,
    isLoading,
    showOtpModal,
    verificationIdentifier,
    verificationType,
    setShowOtpModal,
    handleOtpVerify,
    handleSendOTP,
    getCustomerDetails,
    handleDismissOtpModal,
    resetDismissedState,
    isDismissed,
    // New functions for password/phone verification sequence
    holdPhoneVerification,
    resumePhoneVerification,
    checkPhoneVerification,
  };
};
